import { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { getCachedSignedUrl } from '../services/imageCacheService';
import Avatar, { Props as AvatarProps } from './Avatar';

/**
 * AuthenticatedAvatar component
 * 
 * For private buckets, this component converts authenticated URLs to signed URLs
 * to allow them to be displayed in Avatar components without requiring auth headers.
 * Uses image caching to avoid regenerating signed URLs on every mount.
 * 
 * Usage:
 * <AuthenticatedAvatar 
 *   src={photo_url} 
 *   alt="Venue photo" 
 * />
 */
interface AuthenticatedAvatarProps extends AvatarProps {
  src?: string;
  expiresIn?: number; // Expiration time in seconds (default: 1 hour)
}

export default function AuthenticatedAvatar({ 
  src, 
  expiresIn = 3600,
  ...other 
}: AuthenticatedAvatarProps) {
  const [imageUrl, setImageUrl] = useState<string | undefined>(src || undefined);

  useEffect(() => {
    if (!src) {
      setImageUrl(undefined);
      return;
    }

    // If URL contains /object/authenticated/, convert to signed URL with caching
    if (src.includes('/storage/v1/object/authenticated/')) {
      (async () => {
        try {
          // Extract bucket and path from authenticated URL
          // Format: https://project.supabase.co/storage/v1/object/authenticated/bucket/path
          const match = src.match(/\/storage\/v1\/object\/authenticated\/([^/]+)\/(.+)$/);
          
          if (match) {
            const [, bucket, path] = match;
            
            // Decode the path (handles URL encoding)
            const decodedPath = decodeURIComponent(path);
            
            // Use cached signed URL or generate new one
            const signedUrl = await getCachedSignedUrl(
              src,
              async () => {
                // Generate signed URL
                const { data, error } = await supabase.storage
                  .from(bucket)
                  .createSignedUrl(decodedPath, expiresIn);
                
                if (error) {
                  console.error('Error creating signed URL:', error);
                  console.error('Path:', decodedPath, 'Bucket:', bucket);
                  
                  // Try to find a matching file by filename pattern if exact match fails
                  // Extract filename from path (last part after /)
                  const filename = decodedPath.split('/').pop();
                  if (filename && filename.includes('-')) {
                    // Extract suffix (everything after the hash)
                    const suffix = filename.substring(filename.indexOf('-') + 1);
                    const entityId = decodedPath.split('/')[0];
                    
                    // Try to find a file with matching suffix in the same folder
                    try {
                      const { data: files, error: listError } = await supabase.storage
                        .from(bucket)
                        .list(entityId, {
                          limit: 100,
                        });
                      
                      if (!listError && files && files.length > 0) {
                        // Find a file with matching suffix
                        const matchingFile = files.find(file => 
                          file.name && file.name.includes('-') && 
                          file.name.substring(file.name.indexOf('-') + 1) === suffix
                        );
                        
                        if (matchingFile) {
                          const matchingPath = `${entityId}/${matchingFile.name}`;
                          const { data: signedData, error: signedError } = await supabase.storage
                            .from(bucket)
                            .createSignedUrl(matchingPath, expiresIn);
                          
                          if (!signedError && signedData) {
                            return signedData.signedUrl;
                          }
                        }
                      }
                    } catch (listErr) {
                      console.error('Error searching for matching file:', listErr);
                    }
                  }
                  
                  // Fallback to original URL
                  return src;
                }
                
                return data.signedUrl;
              },
              expiresIn
            );
            
            setImageUrl(signedUrl);
          } else {
            setImageUrl(src);
          }
        } catch (error) {
          console.error('Error generating signed URL:', error);
          setImageUrl(src);
        }
      })();
    } else {
      // For public URLs or other URLs, use as is
      setImageUrl(src);
    }
  }, [src, expiresIn]);

  return <Avatar src={imageUrl} {...other} />;
}

